import type { StoreApi, UseBoundStore } from "zustand";
import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { LoggerContract } from "@core/foundation/logger";
import { isNullish } from "@core/foundation/types";
import { z } from "@core/zod";

import type { StoreSelectors } from "../../zustand/index.ts";
import {
  createStoreProxy,
  createStoreSelectors,
  resetOnMigration,
} from "../../zustand/index.ts";

const AuthStateSchema = z.object(
  { authToken: z.string().nullable() },
  { authToken: "The signed-in user's token, or null when signed out" },
);

interface AuthState extends z.infer<typeof AuthStateSchema> {}

interface AuthActions {
  readonly clearToken: () => void;
  readonly setToken: (authToken: string) => void;
}

export interface AuthStore extends AuthActions, AuthState {}

interface AuthStoreConfig {
  readonly logger: LoggerContract;
  readonly storeKey: string;
  readonly storeVersion: number;
}

interface AuthStoreResult {
  readonly authSelectors: StoreSelectors<AuthState>;
  readonly authStore: AuthStore;
  readonly useAuthStore: UseBoundStore<StoreApi<AuthStore>>;
}

export function createAuthStore({
  logger,
  storeKey,
  storeVersion,
}: AuthStoreConfig): AuthStoreResult {
  const useAuthStore = create<AuthStore>()(
    persist(
      set => {
        return {
          authToken: null,
          clearToken: () => {
            set({ authToken: null });
          },
          setToken: authToken => {
            set({ authToken });
          },
        };
      },
      {
        merge: (persistedState, currentState) => {
          if (isNullish(persistedState)) {
            return currentState;
          }

          const { data, error } = AuthStateSchema.safeParse(persistedState);

          if (error) {
            logger.error("Failed to merge state", error);

            return currentState;
          }

          return { ...currentState, ...data };
        },
        migrate: resetOnMigration,
        name: storeKey,
        partialize: state => {
          const { authToken } = state;

          return { authToken } satisfies AuthState;
        },
        version: storeVersion,
      },
    ),
  );

  const authStore = createStoreProxy(useAuthStore.getState);

  const authSelectors = createStoreSelectors<AuthState>();

  return { authSelectors, authStore, useAuthStore };
}
